import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import { mergeApiRequests } from '@app/core/utilities/api.utilities';
import {
  ApiAccessLevel,
  ApiDeleteRequest,
  ApiDeleteResponse,
  ApiEndpoint,
  ApiGetListRequest,
  ApiGetListResponse,
  ApiGetObjectRequest,
  ApiGetObjectResponse,
  ApiPatchRequest,
  ApiPatchResponse,
  ApiPostRequest,
  ApiPostResponse,
  ApiPutRequest,
  ApiPutResponse,
  ApiRequest,
  ApiRoute,
} from '../models/api.models';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class ApiInstanceService {
  protected get apiRoute(): ApiRoute {
    return [];
  }

  readonly endpoint: ApiEndpoint;

  protected readonly accessLevel?: ApiAccessLevel;

  constructor(protected api: ApiService) {}

  getList<T extends object>(req: Partial<ApiGetListRequest<T>>): Observable<ApiGetListResponse<T>> {
    return this.api.getList(this.requestToApi(req)).pipe(map((response) => this.transformList(response)));
  }

  getAll<T extends object>(req: Partial<ApiGetListRequest<T>> = {}): Observable<T[]> {
    const list: T[] = [];
    req = { ...req, limit: 10000 };

    const getList = (response: ApiGetListResponse<T>): Observable<T[]> => {
      list.push(...response.data);

      const pagination = response.meta.pagination;

      if (pagination && pagination?.total > pagination?.offset + pagination?.limit) {
        return this.getList({ ...req, offset: pagination.offset + pagination.limit }).pipe(
          switchMap((response) => getList(response)),
        );
      }

      return of(list);
    };

    return this.getList(req).pipe(switchMap((response) => getList(response)));
  }

  getObject<T extends object>(req?: Partial<ApiGetObjectRequest<T>>): Observable<ApiGetObjectResponse<T>> {
    return this.api.getObject(this.requestToApi(req || {})).pipe(map((response) => this.transformObject(response)));
  }

  post<T extends object>(req: Partial<ApiPostRequest<T>> = {}): Observable<ApiPostResponse<T>> {
    return this.api.post(this.requestToApi(req));
  }

  put<T extends object>(req: Partial<ApiPutRequest<T>>): Observable<ApiPutResponse> {
    return this.api.put(this.requestToApi(req));
  }

  patch<T extends object>(req: Partial<ApiPatchRequest<T>>): Observable<ApiPatchResponse<T>> {
    return this.api.patch(this.requestToApi(req));
  }

  delete<T extends object>(req: Partial<ApiDeleteRequest<T>>): Observable<ApiDeleteResponse> {
    return this.api.delete(this.requestToApi(req));
  }

  protected transformObject(response: ApiGetObjectResponse<any>): ApiGetObjectResponse<any> {
    return response;
  }

  protected transformList(response: ApiGetListResponse<any>): ApiGetListResponse<any> {
    return response;
  }

  private requestToApi<T extends ApiRequest>(req: Partial<T>): T {
    return mergeApiRequests(
      {
        endpoint: this.endpoint,
        route: this.apiRoute,
        accessLevel: this.accessLevel,
      } as Partial<T>,
      req,
    );
  }
}
